import { useMemo } from 'react';
import { lengthenId, shortenId } from '@beamible/uuid';
import useDesignsSupporters from '@pkg/api/designs/queries/useDesignsSupporters';
import Database from '@pkg/database/Database';

/**
 * Who supports each group across the designs given, as
 * `Map<groupId, Set<userId>>`.
 *
 * Naming no designs means every design the client holds, which is what
 * hydration means by it: the caller either narrows to some or takes what is
 * there. An empty list is a narrowing to none.
 *
 * One request per design, so the map a view over several needs is the union of
 * what each answered, and a write to one group refreshes only the design it
 * belongs to. Ids are held short and named whole by the route, so they cross the
 * boundary in both directions.
 *
 * A scenario carries the ids of the design it came from, which is why two
 * designs can name the same group and their supporters merge.
 *
 * @param {String[]} [designIds]
 * @return {Object}
 */
export default function useSupporterMap(designIds) {
  const stored = Database.useLive('designs');

  const held = useMemo(() => {
    if (designIds !== undefined) return designIds;

    return (stored ?? []).map(({ uuid }) => uuid);
  }, [designIds, stored]);

  const results = useDesignsSupporters(held.map(lengthenId));

  const isLoading = results.some((result) => result.isLoading);
  const settled = results.map((result) => result.dataUpdatedAt).join();

  const supporterMap = useMemo(() => {
    const map = new Map();

    results.forEach((result) => {
      Object.entries(result.data?.data ?? {}).forEach(([groupId, users]) => {
        const supporters = map.get(shortenId(groupId)) ?? new Set();
        users.forEach(({ id }) => supporters.add(id));
        map.set(shortenId(groupId), supporters);
      });
    });

    return map;
  }, [settled, held]);

  return { isLoading, supporterMap };
}
